/**
 * Transaction Filters Component
 *
 * Filter controls for transactions including date range,
 * amount range, merchant search, and category dropdowns.
 *
 * @fileoverview Transaction filter controls component
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  HStack,
  VStack,
  Input,
  Button,
  FormControl,
  FormLabel,
  Select,
  IconButton,
  Collapse,
  useDisclosure,
  Text,
  InputGroup,
  InputLeftElement,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  MdFilterList,
  MdClose,
  MdSearch,
  MdExpandMore,
  MdExpandLess,
} from 'react-icons/md';
import { TransactionFilters as TransactionFiltersType } from '../../types/transactions';

/**
 * Props for TransactionFilters component
 */
interface TransactionFiltersProps {
  filters: TransactionFiltersType;
  onApplyFilters: (filters: TransactionFiltersType) => void;
  onClearFilters: () => void;
  pfcPrimaryOptions?: string[];
  pfcDetailedOptions?: string[];
}

/**
 * Transaction Filters Component
 *
 * Provides filter controls for:
 * - Date range (from/to)
 * - Amount range (min/max)
 * - Merchant name search
 * - PFC Primary dropdown
 * - PFC Detailed dropdown
 * - Category dropdown
 */
const TransactionFilters: React.FC<TransactionFiltersProps> = ({
  filters,
  onApplyFilters,
  onClearFilters,
  pfcPrimaryOptions = [],
  pfcDetailedOptions = [],
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: false });

  // Local state for filter inputs
  const [localFilters, setLocalFilters] = useState<TransactionFiltersType>(filters);
  const [merchantSearch, setMerchantSearch] = useState(filters.merchantName || '');

  // Sync with external filters
  useEffect(() => {
    setLocalFilters(filters);
    setMerchantSearch(filters.merchantName || '');
  }, [filters]);

  /**
   * Handle input change
   */
  const handleChange = useCallback(
    (field: keyof TransactionFiltersType, value: string | number | undefined) => {
      setLocalFilters((prev) => ({
        ...prev,
        [field]: value !== undefined && value !== '' ? value : undefined,
      }));
    },
    []
  );

  /**
   * Handle merchant search with debounce
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (merchantSearch !== localFilters.merchantName) {
        handleChange('merchantName', merchantSearch || undefined);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [merchantSearch, localFilters.merchantName, handleChange]);

  /**
   * Apply filters
   */
  const handleApply = useCallback(() => {
    onApplyFilters(localFilters);
  }, [localFilters, onApplyFilters]);

  /**
   * Clear all filters
   */
  const handleClear = useCallback(() => {
    setLocalFilters({});
    setMerchantSearch('');
    onClearFilters();
  }, [onClearFilters]);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters =
    filters.dateFrom ||
    filters.dateTo ||
    filters.amountMin !== undefined ||
    filters.amountMax !== undefined ||
    filters.merchantName ||
    filters.pfcPrimary ||
    filters.pfcDetailed ||
    filters.category ||
    filters.transactionClass;

  return (
    <Box>
      {/* Filter toggle button */}
      <HStack justify="space-between" mb={2}>
        <Button
          leftIcon={<MdFilterList />}
          rightIcon={isOpen ? <MdExpandLess /> : <MdExpandMore />}
          variant="ghost"
          size="sm"
          color="text.secondary"
          onClick={onToggle}
          _hover={{ bg: 'xacto.mutedNavy' }}
        >
          Filters
          {hasActiveFilters && (
            <Box
              as="span"
              ml={2}
              px={2}
              py={0.5}
              bg="xacto.electricCyan"
              color="xacto.deepNavy"
              fontSize="xs"
              borderRadius="full"
              fontWeight="bold"
            >
              Active
            </Box>
          )}
        </Button>

        {hasActiveFilters && (
          <Button
            leftIcon={<MdClose />}
            variant="ghost"
            size="xs"
            color="text.muted"
            onClick={handleClear}
            _hover={{ color: 'xacto.pureWhite' }}
          >
            Clear all
          </Button>
        )}
      </HStack>

      {/* Filter controls */}
      <Collapse in={isOpen} animateOpacity>
        <Box
          p={4}
          bg="bg.surface"
          borderRadius="md"
          borderWidth="1px"
          borderColor="xacto.borderGray"
          mb={4}
        >
          <Wrap spacing={4} align="flex-end">
            {/* Date From */}
            <WrapItem>
              <FormControl w="140px">
                <FormLabel fontSize="xs" color="text.muted" mb={1}>
                  Date From
                </FormLabel>
                <Input
                  type="date"
                  size="sm"
                  value={localFilters.dateFrom || ''}
                  onChange={(e) => handleChange('dateFrom', e.target.value)}
                  bg="bg.primary"
                  borderColor="xacto.borderGray"
                  color="text.primary"
                  _hover={{ borderColor: 'xacto.electricCyan' }}
                  _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
                />
              </FormControl>
            </WrapItem>

            {/* Date To */}
            <WrapItem>
              <FormControl w="140px">
                <FormLabel fontSize="xs" color="text.muted" mb={1}>
                  Date To
                </FormLabel>
                <Input
                  type="date"
                  size="sm"
                  value={localFilters.dateTo || ''}
                  onChange={(e) => handleChange('dateTo', e.target.value)}
                  bg="bg.primary"
                  borderColor="xacto.borderGray"
                  color="text.primary"
                  _hover={{ borderColor: 'xacto.electricCyan' }}
                  _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
                />
              </FormControl>
            </WrapItem>

            {/* Amount Min */}
            <WrapItem>
              <FormControl w="100px">
                <FormLabel fontSize="xs" color="text.muted" mb={1}>
                  Min Amount
                </FormLabel>
                <Input
                  type="number"
                  size="sm"
                  placeholder="0.00"
                  value={localFilters.amountMin ?? ''}
                  onChange={(e) =>
                    handleChange(
                      'amountMin',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                  bg="bg.primary"
                  borderColor="xacto.borderGray"
                  color="text.primary"
                  _hover={{ borderColor: 'xacto.electricCyan' }}
                  _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
                />
              </FormControl>
            </WrapItem>

            {/* Amount Max */}
            <WrapItem>
              <FormControl w="100px">
                <FormLabel fontSize="xs" color="text.muted" mb={1}>
                  Max Amount
                </FormLabel>
                <Input
                  type="number"
                  size="sm"
                  placeholder="0.00"
                  value={localFilters.amountMax ?? ''}
                  onChange={(e) =>
                    handleChange(
                      'amountMax',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                  bg="bg.primary"
                  borderColor="xacto.borderGray"
                  color="text.primary"
                  _hover={{ borderColor: 'xacto.electricCyan' }}
                  _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
                />
              </FormControl>
            </WrapItem>

            {/* Merchant Search */}
            <WrapItem>
              <FormControl w="180px">
                <FormLabel fontSize="xs" color="text.muted" mb={1}>
                  Merchant
                </FormLabel>
                <InputGroup size="sm">
                  <InputLeftElement>
                    <MdSearch color="gray" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search..."
                    value={merchantSearch}
                    onChange={(e) => setMerchantSearch(e.target.value)}
                    bg="bg.primary"
                    borderColor="xacto.borderGray"
                    color="text.primary"
                    _hover={{ borderColor: 'xacto.electricCyan' }}
                    _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
                  />
                </InputGroup>
              </FormControl>
            </WrapItem>

            {/* PFC Primary */}
            <WrapItem>
              <FormControl w="150px">
                <FormLabel fontSize="xs" color="text.muted" mb={1}>
                  PFC Primary
                </FormLabel>
                <Select
                  size="sm"
                  placeholder="All"
                  value={localFilters.pfcPrimary || ''}
                  onChange={(e) => handleChange('pfcPrimary', e.target.value)}
                  bg="bg.primary"
                  borderColor="xacto.borderGray"
                  color="text.primary"
                  _hover={{ borderColor: 'xacto.electricCyan' }}
                  _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
                >
                  {pfcPrimaryOptions.map((opt) => (
                    <option key={opt} value={opt} style={{ background: '#1a1f36' }}>
                      {opt}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </WrapItem>

            {/* PFC Detailed */}
            <WrapItem>
              <FormControl w="150px">
                <FormLabel fontSize="xs" color="text.muted" mb={1}>
                  PFC Detailed
                </FormLabel>
                <Select
                  size="sm"
                  placeholder="All"
                  value={localFilters.pfcDetailed || ''}
                  onChange={(e) => handleChange('pfcDetailed', e.target.value)}
                  bg="bg.primary"
                  borderColor="xacto.borderGray"
                  color="text.primary"
                  _hover={{ borderColor: 'xacto.electricCyan' }}
                  _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
                >
                  {pfcDetailedOptions.map((opt) => (
                    <option key={opt} value={opt} style={{ background: '#1a1f36' }}>
                      {opt}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </WrapItem>



            {/* Transaction Class */}
            <WrapItem>
              <FormControl w="120px">
                <FormLabel fontSize="xs" color="text.muted" mb={1}>
                  Classification
                </FormLabel>
                <Select
                  size="sm"
                  placeholder="All"
                  value={localFilters.transactionClass || ''}
                  onChange={(e) => handleChange('transactionClass', e.target.value || undefined)}
                  bg="bg.primary"
                  borderColor="xacto.borderGray"
                  color="text.primary"
                  _hover={{ borderColor: 'xacto.electricCyan' }}
                  _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
                >
                  <option value="INCOME" style={{ background: '#1a1f36' }}>
                    Income
                  </option>
                  <option value="EXPENSE" style={{ background: '#1a1f36' }}>
                    Expense
                  </option>
                </Select>
              </FormControl>
            </WrapItem>

            {/* Apply Button */}
            <WrapItem>
              <Button
                size="sm"
                colorScheme="cyan"
                bg="xacto.electricCyan"
                color="xacto.deepNavy"
                onClick={handleApply}
                _hover={{ bg: 'cyan.300' }}
              >
                Apply
              </Button>
            </WrapItem>
          </Wrap>
        </Box>
      </Collapse>
    </Box>
  );
};

export default TransactionFilters;
